import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import CVForm from "@/components/CVForm";
import CVPreview from "@/components/CVPreview";
import PaymentModal from "@/components/PaymentModal";
import { Button } from "@/components/ui/button";
import { Download, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useReactToPrint } from "react-to-print";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuilderPage = () => {
  const navigate = useNavigate();
  const [cvData, setCvData] = useState(() => {
    const saved = localStorage.getItem('cvData');
    return saved ? JSON.parse(saved) : {
      fullName: "",
      email: "",
      phone: "",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      dateOfBirth: "",
      nationality: "",
      idNumber: "",
      languagesSpoken: "",
      linkedin: "",
      website: "",
      summary: "",
      workExperience: [{ role: "", company: "", startDate: "", endDate: "", description: "" }],
      education: [{ degree: "", institution: "", graduationYear: "", subjects: "" }],
      skills: [],
      languages: [],
      achievements: [],
      attributes: [],
      references: [],
      template: "professional",
      photo: ""
    };
  });
  const [showPayment, setShowPayment] = useState(false);
  const [cvId, setCvId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const cvPreviewRef = useRef();

  useEffect(() => {
    localStorage.setItem('cvData', JSON.stringify(cvData));
  }, [cvData]);

  const handleProceedToPayment = async () => {
    if (!cvData.fullName || !cvData.email) {
      toast.error("Please fill in at least your name and email");
      return;
    }

    try {
      const response = await axios.post(`${API}/cv/create`, { cvData });
      setCvId(response.data.id);
      setShowPayment(true);
      toast.success("CV saved! Proceed to payment.");
    } catch (error) {
      toast.error("Failed to save CV. Please try again.");
      console.error(error);
    }
  };

  const handlePaymentSuccess = (reference) => {
    localStorage.removeItem('cvData');
    navigate(`/success?reference=${reference}`);
  };

  const handlePrint = useReactToPrint({
    content: () => cvPreviewRef.current,
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop: Split Layout */}
      <div className="hidden lg:grid lg:grid-cols-[45%_55%] h-screen">
        {/* Left Panel: Form */}
        <div className="bg-background overflow-y-auto p-8">
          <div className="max-w-2xl">
            <div className="mb-8">
              <h1 className="font-heading text-3xl font-light mb-2">Build Your CV</h1>
              <p className="text-sm text-muted-foreground">Fill in your details. See it come to life.</p>
            </div>
            <CVForm cvData={cvData} setCvData={setCvData} />
            <div className="mt-8">
              <Button
                data-testid="proceed-payment-btn"
                onClick={handleProceedToPayment}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-none h-12 px-8 font-medium tracking-wide w-full transition-transform hover:-translate-y-0.5 active:scale-95"
              >
                Proceed to Payment ($9.99)
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel: Preview */}
        <div className="bg-preview-bg flex items-start justify-center overflow-y-auto p-8 sticky top-0">
          <div className="transform scale-75 md:scale-90 origin-top transition-transform duration-500">
            <CVPreview ref={cvPreviewRef} cvData={cvData} />
          </div>
        </div>
      </div>

      {/* Mobile: Stacked */}
      <div className="lg:hidden">
        <div className="p-6">
          <div className="mb-6">
            <h1 className="font-heading text-3xl font-light mb-2">Build Your CV</h1>
            <p className="text-sm text-muted-foreground">Fill in your details below.</p>
          </div>
          <CVForm cvData={cvData} setCvData={setCvData} />
          <div className="mt-6 space-y-3">
            <Button
              data-testid="preview-cv-mobile"
              onClick={() => setShowPreview(!showPreview)}
              variant="outline"
              className="w-full rounded-none h-12 border-border"
            >
              <Eye className="mr-2 h-4 w-4" />
              {showPreview ? "Hide Preview" : "Show Preview"}
            </Button>
            {showPreview && (
              <div className="bg-preview-bg p-4 overflow-x-auto">
                <div className="transform scale-50 origin-top-left" style={{ width: "200%" }}>
                  <CVPreview ref={cvPreviewRef} cvData={cvData} />
                </div>
              </div>
            )}
            <Button
              data-testid="proceed-payment-btn-mobile"
              onClick={handleProceedToPayment}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-none h-12 px-8 font-medium tracking-wide w-full"
            >
              Proceed to Payment ($9.99)
            </Button>
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentModal
          isOpen={showPayment}
          onClose={() => setShowPayment(false)}
          cvData={cvData}
          cvId={cvId}
          onSuccess={handlePaymentSuccess}
        />
      )}
      
      {/* Footer */}
      <footer className="py-4 px-6 border-t border-border/40 bg-secondary/20 mt-auto">
        <div className="max-w-7xl mx-auto flex justify-center gap-6 text-xs text-muted-foreground">
          <Link to="/refund-policy" className="hover:text-foreground transition-colors">
            Refund Policy
          </Link>
          <a href="mailto:support@instantcvforge.com" className="hover:text-foreground transition-colors">
            Support
          </a>
        </div>
      </footer>
    </div>
  );
};

export default BuilderPage;